import { createEffect, createSignal, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Eye, Lock, MapPin, Save, Shield } from "lucide-solid";

import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import {
  asBool,
  DEFAULT_PROFILE_SETTINGS,
  getCollectorProfileQuery,
  type InteractionPermission,
  type ProfileVisibility,
  queryKeys,
  saveProfileSettingsMutation,
} from "@famidex/shared";
import { PageHeader } from "@/components/ui/page-header";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";

/** Valida el valor de visibilidad guardado (columna TEXT) sin asertos. */
function parseVisibility(value: unknown, fallback: ProfileVisibility): ProfileVisibility {
  return value === "everyone" || value === "followers" || value === "private" ? value : fallback;
}

/** Valida el permiso de interacción guardado (columna TEXT) sin asertos. */
function parsePermission(value: unknown, fallback: InteractionPermission): InteractionPermission {
  return value === "everyone" || value === "followers" || value === "nobody" ? value : fallback;
}

/**
 * 🔒 CONFIGURAR PRIVACIDAD DE PERFIL — traducción del diseño Stitch
 * `configurar_privacidad_de_perfil`. Edita los ajustes de `collector_profile`
 * (stream privado `my_profile`): perfil privado, visibilidad de colección/deseos,
 * ubicación y permisos de interacción. Guarda con UPDATE-or-INSERT (el servidor
 * estampa el user_id). La lógica de qué implica cada ajuste vive PURA en
 * `@famidex/shared` (`canViewCollection`, …) y está cubierta por tests.
 */
export function PrivacySettingsPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const userId = session.userId;

  const profile = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle() && userId()),
    queryKey: () => queryKeys.collectorProfile.settings(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getCollectorProfileQuery(d, uid) : null;
    },
  });

  const [isPrivate, setIsPrivate] = createSignal<boolean>(DEFAULT_PROFILE_SETTINGS.is_private);
  const [collectionVis, setCollectionVis] = createSignal<ProfileVisibility>(
    DEFAULT_PROFILE_SETTINGS.collection_visibility,
  );
  const [wishlistVis, setWishlistVis] = createSignal<ProfileVisibility>(
    DEFAULT_PROFILE_SETTINGS.wishlist_visibility,
  );
  const [showLocation, setShowLocation] = createSignal<boolean>(
    DEFAULT_PROFILE_SETTINGS.show_location,
  );
  const [commentPerm, setCommentPerm] = createSignal<InteractionPermission>(
    DEFAULT_PROFILE_SETTINGS.comment_permission,
  );
  const [tagPerm, setTagPerm] = createSignal<InteractionPermission>(
    DEFAULT_PROFILE_SETTINGS.tag_permission,
  );
  const [saving, setSaving] = createSignal(false);

  // Hidrata el formulario desde la fila local-first MIENTRAS el usuario no lo
  // haya tocado — y NUNCA pisa una edición suya (su valor MANDA desde el primer
  // toque). Seguir hidratando (no one-shot) importa: tras recargar, PowerSync
  // puede servir brevemente un checkpoint ANTERIOR al último guardado (lag de
  // replicación) y el valor definitivo llega por sync un instante después; un
  // one-shot dejaba el formulario clavado en ese estado transitorio.
  let touched = false;
  const touch = () => {
    touched = true;
  };
  createEffect(() => {
    const row = profile.data?.[0];
    if (!row || touched) return;
    setIsPrivate(asBool(row.is_private));
    setCollectionVis(parseVisibility(row.collection_visibility, "everyone"));
    setWishlistVis(parseVisibility(row.wishlist_visibility, "followers"));
    setShowLocation(asBool(row.show_location));
    setCommentPerm(parsePermission(row.comment_permission, "everyone"));
    setTagPerm(parsePermission(row.tag_permission, "followers"));
  });

  const visibilityOptions = (): { value: ProfileVisibility; label: string }[] => [
    { value: "everyone", label: t(msg`Todos`) },
    { value: "followers", label: t(msg`Solo Seguidores`) },
    { value: "private", label: t(msg`Solo Yo`) },
  ];
  const interactionOptions = (): { value: InteractionPermission; label: string }[] => [
    { value: "everyone", label: t(msg`Todos`) },
    { value: "followers", label: t(msg`Solo Seguidores`) },
    { value: "nobody", label: t(msg`Nadie`) },
  ];

  const save = async () => {
    const d = drizzle();
    const uid = userId();
    if (!d || !uid || saving()) return;
    setSaving(true);
    try {
      await saveProfileSettingsMutation(d, uid, {
        is_private: isPrivate(),
        collection_visibility: collectionVis(),
        wishlist_visibility: wishlistVis(),
        show_location: showLocation(),
        comment_permission: commentPerm(),
        tag_permission: tagPerm(),
      });
      showToast(t(msg`Privacidad actualizada`), "success");
    } catch {
      showToast(t(msg`No se pudo guardar. Inténtalo de nuevo.`), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="flex flex-col gap-5" data-testid="privacy-settings-page">
      <PageHeader title={t(msg`Privacidad del Perfil`)} backTo="/journal" icon={Lock} />

      {/* Visibilidad del perfil */}
      <section class="rounded-xl border border-border/60 bg-card p-4">
        <h2 class="mb-3 flex items-center gap-2 text-base font-bold text-foreground">
          <span class="grid h-8 w-8 place-items-center rounded-full bg-muted text-primary">
            <Lock class="h-4 w-4" />
          </span>
          {t(msg`Visibilidad del Perfil`)}
        </h2>
        <div class="flex items-start justify-between gap-4 rounded-lg bg-default p-3">
          <div>
            <p class="font-semibold text-foreground">{t(msg`Perfil Privado`)}</p>
            <p class="mt-0.5 text-sm text-muted-foreground">
              {t(
                msg`Solo los seguidores aprobados podrán ver tu colección, tus deseos y tus publicaciones.`,
              )}
            </p>
          </div>
          <Switch
            checked={isPrivate()}
            onCheckedChange={(v) => {
              touch();
              setIsPrivate(v);
            }}
            data-testid="toggle-private"
          />
        </div>
      </section>

      {/* Permisos de contenido */}
      <section class="rounded-xl border border-border/60 bg-card p-4">
        <h2 class="mb-4 flex items-center gap-2 text-base font-bold text-foreground">
          <span class="grid h-8 w-8 place-items-center rounded-full bg-danger/15 text-danger">
            <Eye class="h-4 w-4" />
          </span>
          {t(msg`Permisos de Contenido`)}
        </h2>
        <div class="flex flex-col gap-4">
          <Field label={t(msg`Ver mi Colección`)}>
            <Select
              options={visibilityOptions()}
              value={collectionVis()}
              onChange={(v) => {
                // Kobalte re-emite el valor ACTUAL al reconciliar el valor
                // controlado en el montaje: sólo un cambio REAL marca `touched`.
                if (v !== collectionVis()) {
                  touch();
                  setCollectionVis(v);
                }
              }}
              aria-label={t(msg`Ver mi Colección`)}
            />
          </Field>
          <Field label={t(msg`Ver mis Deseos`)}>
            <Select
              options={visibilityOptions()}
              value={wishlistVis()}
              onChange={(v) => {
                if (v !== wishlistVis()) {
                  touch();
                  setWishlistVis(v);
                }
              }}
              aria-label={t(msg`Ver mis Deseos`)}
            />
          </Field>
          <div class="flex items-center justify-between gap-4 rounded-lg bg-default p-3">
            <span class="flex items-center gap-2 font-medium text-foreground">
              <MapPin class="h-4 w-4 text-muted-foreground" />
              {t(msg`Mostrar mi ubicación`)}
            </span>
            <Switch
              checked={showLocation()}
              onCheckedChange={(v) => {
                touch();
                setShowLocation(v);
              }}
              data-testid="toggle-location"
            />
          </div>
        </div>
      </section>

      {/* Interacciones */}
      <section class="rounded-xl border border-border/60 bg-card p-4">
        <h2 class="mb-4 flex items-center gap-2 text-base font-bold text-foreground">
          <span class="grid h-8 w-8 place-items-center rounded-full bg-primary/15 text-primary">
            <Shield class="h-4 w-4" />
          </span>
          {t(msg`Interacciones`)}
        </h2>
        <div class="flex flex-col gap-4">
          <Field label={t(msg`Quién puede comentarme`)}>
            <Select
              options={interactionOptions()}
              value={commentPerm()}
              onChange={(v) => {
                if (v !== commentPerm()) {
                  touch();
                  setCommentPerm(v);
                }
              }}
              aria-label={t(msg`Quién puede comentarme`)}
            />
          </Field>
          <Field label={t(msg`Quién puede etiquetarme`)}>
            <Select
              options={interactionOptions()}
              value={tagPerm()}
              onChange={(v) => {
                if (v !== tagPerm()) {
                  touch();
                  setTagPerm(v);
                }
              }}
              aria-label={t(msg`Quién puede etiquetarme`)}
            />
          </Field>
        </div>
      </section>

      <button
        type="button"
        onClick={() => void save()}
        disabled={saving()}
        data-testid="save-privacy"
        class="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-bold text-primary-foreground shadow-lg shadow-primary/20 transition-opacity disabled:opacity-60"
      >
        <Save class="h-5 w-5" />
        <Show when={!saving()} fallback={t(msg`Guardando…`)}>
          {t(msg`Guardar Cambios`)}
        </Show>
      </button>
    </div>
  );
}

function Field(props: { label: string; children: JSX.Element }) {
  return (
    <label class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-foreground">{props.label}</span>
      {props.children}
    </label>
  );
}
